// components/playground/Header/ScenarioOverflowMenu.jsx - Mobile Responsive

import React, { useState, useRef, useEffect } from 'react';
import { 
  EllipsisVerticalIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  ArrowUturnLeftIcon,
  TrashIcon,
  RectangleStackIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const ScenarioOverflowMenu = ({
  isBaseline,
  hasUnsavedChanges,
  onRename,
  onSaveAsNew,
  onDiscardChanges,
  onDelete,
  onViewDetails,
  onUpdateScenario,
  onRefreshScenarios,
  isRefreshing,
  isSaving,
  isMobile = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleAction = (action) => {
    setIsOpen(false);
    action();
  };

  // Menu items configuration
  const menuItems = [
    {
      id: 'refresh',
      icon: ArrowPathIcon,
      label: isRefreshing ? 'Refreshing...' : 'Refresh Scenarios',
      action: onRefreshScenarios,
      disabled: isRefreshing,
      spinning: isRefreshing,
      className: 'text-gray-700',
      show: true,
    },
    { type: 'divider', show: true },
    {
      id: 'save',
      icon: CloudArrowUpIcon,
      label: isSaving ? 'Saving...' : 'Save Changes',
      action: onUpdateScenario,
      disabled: isSaving,
      className: 'text-green-700 font-semibold',
      bgHover: 'hover:bg-green-50',
      show: !isBaseline && hasUnsavedChanges,
    },
    { type: 'divider', show: !isBaseline && hasUnsavedChanges },
    {
      id: 'rename',
      icon: PencilIcon,
      label: 'Rename Scenario',
      action: onRename,
      className: 'text-gray-700',
      show: !isBaseline,
    },
    {
      id: 'saveAsNew',
      icon: DocumentDuplicateIcon,
      label: 'Save as New Scenario',
      action: onSaveAsNew,
      className: 'text-gray-700',
      show: true,
    },
    { type: 'divider', show: hasUnsavedChanges },
    {
      id: 'discard',
      icon: ArrowUturnLeftIcon,
      label: 'Discard Changes',
      action: onDiscardChanges,
      className: 'text-orange-600',
      bgHover: 'hover:bg-orange-50',
      show: hasUnsavedChanges,
    },
    { type: 'divider', show: !isBaseline },
    {
      id: 'delete',
      icon: TrashIcon,
      label: 'Delete Scenario',
      action: onDelete,
      className: 'text-red-600',
      bgHover: 'hover:bg-red-50',
      show: !isBaseline,
    },
    { type: 'divider', show: true },
    {
      id: 'manage',
      icon: RectangleStackIcon,
      label: 'Manage All Scenarios',
      action: onViewDetails,
      className: 'text-blue-700 font-medium',
      bgHover: 'hover:bg-blue-50',
      show: true,
    },
  ];

  const visibleItems = menuItems.filter(item => item.show);

  return (
    <div className="relative" ref={menuRef}>
      {/* Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          hover:bg-gray-100 rounded-md transition-colors
          ${isMobile ? 'p-2.5' : 'p-2'}
        `}
        title="More actions"
        aria-label="More scenario actions"
      >
        <EllipsisVerticalIcon className={`text-gray-600 ${isMobile ? 'w-6 h-6' : 'w-5 h-5'}`} />
      </button>

      {/* Menu Dropdown */}
      {isOpen && (
        <>
          {/* Mobile: Full-screen backdrop */}
          {isMobile && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-25 z-40"
              onClick={() => setIsOpen(false)}
            />
          )}
          
          {/* Menu content */}
          <div className={`
            ${isMobile 
              ? 'fixed left-0 right-0 bottom-0 z-50 rounded-t-2xl' 
              : 'absolute top-full right-0 mt-1 z-50'
            }
            ${isMobile ? 'w-full' : 'w-64'}
            bg-white border border-gray-200 shadow-lg
            ${!isMobile && 'rounded-lg'}
            overflow-hidden
          `}>
            {/* Mobile: Handle bar and header */}
            {isMobile && (
              <>
                <div className="flex justify-center py-2">
                  <div className="w-12 h-1 bg-gray-300 rounded-full" />
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h3 className="font-semibold text-gray-900">Scenario Actions</h3>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </>
            )}

            {/* Menu items */}
            <div className={isMobile ? 'py-2' : 'py-1'}>
              {visibleItems.map((item, index) => {
                if (item.type === 'divider') {
                  return (
                    <div 
                      key={`divider-${index}`} 
                      className="border-t border-gray-200 my-1" 
                    />
                  );
                }

                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleAction(item.action)}
                    disabled={item.disabled}
                    className={`
                      w-full text-left px-4 transition-colors flex items-center gap-3
                      ${isMobile ? 'py-3.5' : 'py-2.5'}
                      ${item.bgHover || 'hover:bg-gray-50'}
                      ${item.className}
                      ${isMobile ? 'text-base' : 'text-sm'}
                      disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                  >
                    <Icon className={`
                      ${isMobile ? 'w-5 h-5' : 'w-4 h-4'}
                      ${item.spinning ? 'animate-spin' : ''}
                    `} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Mobile: Safe area padding */}
            {isMobile && (
              <div className="h-safe-area-bottom bg-white" />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ScenarioOverflowMenu;